const ICE_READY_FALLBACK_TIMEOUT_MS = 1000;
const ICE_SRFLX_READY_GRACE_MS = 200;
const ICE_CANDIDATE_TYPE_REGEXP = /\btyp\s+([\da-z-]+)\b/i;
const ICE_UFRAG_REGEXP = /\bufrag\s+(\S+)/i;

type EarlyIceCandidateType = 'host' | 'srflx' | 'relay' | 'prflx' | 'unknown';

type EarlyIceCandidateEvent = {
	candidate: RTCIceCandidate;
	ready: VoidFunction;
};

type EarlyIceReadyController = {
	/** Обрабатывает ICE-кандидат и при необходимости завершает ожидание JsSIP. */
	handleIceCandidate: (event: EarlyIceCandidateEvent) => void;
	/** Очищает отложенные таймеры ранней готовности ICE. */
	cancel: () => void;
};

/**
 * Возвращает тип ICE-кандидата из SDP-строки кандидата.
 *
 * @param candidate ICE-кандидат от браузерного RTCPeerConnection.
 * @returns Тип кандидата или `unknown`, если строка не распознана.
 */
const getIceCandidateType = (candidate: RTCIceCandidate): EarlyIceCandidateType => {
	const type = candidate.candidate.match(ICE_CANDIDATE_TYPE_REGEXP)?.[1]?.toLowerCase();

	if (type === 'host' || type === 'srflx' || type === 'relay' || type === 'prflx') {
		return type;
	}

	return 'unknown';
};

/**
 * Возвращает ключ ICE-поколения, чтобы не переносить таймеры между разными сборками кандидатов.
 *
 * @param candidate ICE-кандидат от браузерного RTCPeerConnection.
 * @returns ICE username fragment или стабильный fallback-ключ.
 */
const getIceCandidateGenerationKey = (candidate: RTCIceCandidate): string => {
	return (
		candidate.usernameFragment || candidate.candidate.match(ICE_UFRAG_REGEXP)?.[1] || 'default'
	);
};

/**
 * Создаёт обработчик ранней готовности ICE для JsSIP.
 *
 * @returns Контроллер, который ускоряет отправку SDP и очищает внутренние таймеры.
 */
export const createEarlyIceReadyController = (): EarlyIceReadyController => {
	let generationKey: string | undefined;
	let readyCalled = false;
	let srflxReadyTimer: ReturnType<typeof setTimeout> | undefined;
	let fallbackReadyTimer: ReturnType<typeof setTimeout> | undefined;

	/** Очищает таймеры отложенного вызова `ready`. */
	const clearReadyTimers = (): void => {
		if (srflxReadyTimer) {
			clearTimeout(srflxReadyTimer);
			srflxReadyTimer = undefined;
		}

		if (fallbackReadyTimer) {
			clearTimeout(fallbackReadyTimer);
			fallbackReadyTimer = undefined;
		}
	};

	/**
	 * Сбрасывает состояние, если браузер начал новое ICE-поколение.
	 *
	 * @param candidate ICE-кандидат текущей сборки.
	 */
	const resetForGeneration = (candidate: RTCIceCandidate): void => {
		const nextGenerationKey = getIceCandidateGenerationKey(candidate);

		if (generationKey === nextGenerationKey) return;

		generationKey = nextGenerationKey;
		readyCalled = false;
		clearReadyTimers();
	};

	/**
	 * Однократно сообщает JsSIP, что SDP можно отправлять.
	 *
	 * @param ready Callback JsSIP для завершения ожидания ICE.
	 */
	const markReady = (ready: VoidFunction): void => {
		if (readyCalled) return;

		readyCalled = true;
		clearReadyTimers();
		ready();
	};

	/**
	 * Планирует отправку SDP после `srflx`, оставляя короткое окно для `relay`.
	 *
	 * @param ready Callback JsSIP для завершения ожидания ICE.
	 */
	const scheduleSrflxReady = (ready: VoidFunction): void => {
		if (srflxReadyTimer) return;

		srflxReadyTimer = setTimeout(() => markReady(ready), ICE_SRFLX_READY_GRACE_MS);
	};

	/**
	 * Планирует fallback, если браузер не отдаст внешние ICE-кандидаты.
	 *
	 * @param ready Callback JsSIP для завершения ожидания ICE.
	 */
	const scheduleFallbackReady = (ready: VoidFunction): void => {
		if (fallbackReadyTimer) return;

		fallbackReadyTimer = setTimeout(() => markReady(ready), ICE_READY_FALLBACK_TIMEOUT_MS);
	};

	return {
		handleIceCandidate({ candidate, ready }) {
			resetForGeneration(candidate);

			const candidateType = getIceCandidateType(candidate);

			if (candidateType === 'relay') {
				markReady(ready);
				return;
			}

			if (candidateType === 'srflx') {
				scheduleSrflxReady(ready);
				return;
			}

			scheduleFallbackReady(ready);
		},
		cancel() {
			readyCalled = true;
			generationKey = undefined;
			clearReadyTimers();
		},
	};
};
